/**
 * A fixed-row-height window.
 *
 * Phase 4: "Virtualized table handling 5,000 rows without lag", and
 * `pnpm db:seed:bulk` produces exactly that. Rendering 5,000 rows of ~14 cells
 * is 70,000 DOM nodes and a visibly janky scroll; rendering the ~40 that fit
 * on screen plus an overscan margin is not.
 *
 * Written here rather than taken as a dependency because the requirement is
 * one measurement (`scrollTop`) and two divisions, and every row in this table
 * is the same height by design — a variable-height virtualizer would be a much
 * larger thing to audit for the sake of a feature the table does not have.
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export interface VirtualWindow {
  /** Rows before the window, as a spacer height in pixels. */
  paddingTop: number;
  /** Rows after the window. */
  paddingBottom: number;
  startIndex: number;
  endIndex: number;
}

export interface UseVirtualRowsOptions {
  count: number;
  rowHeight: number;
  /** Rows rendered above and below the viewport, so a fast scroll is not blank. */
  overscan?: number;
}

export function useVirtualRows<T extends HTMLElement>(
  options: UseVirtualRowsOptions,
): { ref: RefObject<T>; window: VirtualWindow } {
  const { count, rowHeight, overscan = 8 } = options;
  const ref = useRef<T>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(800);

  const measure = useCallback(() => {
    const element = ref.current;
    if (element === null) return;
    setScrollTop(element.scrollTop);
    // jsdom reports 0 for every layout measurement, so a test would otherwise
    // render a single row and assert nothing. Fall back to a sane viewport.
    setViewportHeight(element.clientHeight > 0 ? element.clientHeight : 800);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (element === null) return undefined;
    measure();
    element.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  const visibleCount = Math.ceil(viewportHeight / rowHeight) + overscan * 2;
  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const endIndex = Math.min(count, startIndex + visibleCount);

  return {
    ref,
    window: {
      startIndex,
      endIndex,
      paddingTop: startIndex * rowHeight,
      paddingBottom: Math.max(0, (count - endIndex) * rowHeight),
    },
  };
}
